/**
 * Contrato público del store de "Escuchar & Hablar" + el contexto React.
 * La implementación vive en `ListenProvider.tsx`; toda la UI (biblioteca,
 * player, mini-player) consume `useListen()`.
 */

import { createContext, useContext } from 'react';
import type { WordLookupEntry } from '../../components/word-lookup/index.js';
import type {
  Availability,
  CatalogBook,
  CatalogKeypoint,
  Cefr,
  ChapterProgress,
  ChapterRef,
  ChapterStatus,
  Edition,
  KaraokeLayout,
  ProgressEntry,
  Rendition,
} from './types.js';

/** Próximo capítulo a reproducir (preview del panel de fin + auto-avance). */
export interface NextUp {
  book: CatalogBook;
  chapter: CatalogKeypoint;
  edition: Edition;
  /** El siguiente capítulo pertenece a OTRO audiolibro (cruce de libro). */
  crossBook: boolean;
}

/** Modo del temporizador de sueño: fin del capítulo o cuenta regresiva. */
export type SleepMode = 'eoc' | 'timer';

/**
 * Estado de la transcripción (karaoke) de la rendition activa. Existe para
 * separar las DOS causas de "no hay karaoke", que antes se veían idénticas:
 *
 *   - `absent` — la rendition no tiene alignment (`alignmentUrl` null): el
 *     archivo no existe en el contenido o no llegó a `listen_renditions`.
 *   - `error`  — SÍ tiene alignment, pero la bajada falló (red, 404 del
 *     bucket, URL firmada vencida). Es un problema de ENTREGA, reintentable.
 *
 * `idle` = todavía no se resolvió (sin rendition), `loading` = bajando,
 * `ok` = `layout` publicado.
 */
export type AlignmentStatus = 'idle' | 'loading' | 'ok' | 'absent' | 'error';

export interface ListenContextValue {
  // Catálogo de lo que está abierto (display).
  book: CatalogBook | null;
  chapter: CatalogKeypoint | null;
  ref: ChapterRef | null;
  isOpen: boolean;

  // Rendition activa.
  rendition: Rendition | null;
  available: Availability[];
  /** Nivel CEFR pedido (selector). */
  level: Cefr;
  /** Nivel efectivamente cargado (`rendition.cefr`), o null si no hay audio. */
  effLevel: Cefr | null;
  /** Voz pedida (voiceSlug), o null = auto. */
  voiceId: string | null;
  /** Lang de la rendition cargada. */
  lang: string | null;

  // Karaoke.
  layout: KaraokeLayout | null;
  /**
   * Por qué hay (o no hay) `layout`. La UI del player lo usa para decir cuál de
   * las dos cosas pasó — "esta narración todavía no tiene transcripción"
   * (`absent`) vs "no pudimos cargarla" (`error`) — en vez del mensaje único
   * que hacía invisible un fallo de entrega. Ver `AlignmentStatus`.
   */
  alignmentStatus: AlignmentStatus;
  /**
   * Reintenta bajar la transcripción de la rendition activa (CTA del estado
   * `error`). Re-firma la URL contra el backend; no toca el `<audio>`, así que
   * el reintento no interrumpe la reproducción.
   */
  retryAlignment: () => void;
  karaoke: boolean;
  /**
   * Glosario del capítulo (tap-to-lookup): mapa `normWord → entrada`. Pre-
   * fetcheado al abrir una rendition EN, así el popover lee local (cero LLM por
   * tap). `null` mientras carga o en renditions no-EN (el tap se deshabilita).
   */
  glossary: Record<string, WordLookupEntry> | null;

  // Playback.
  pos: number;
  /**
   * Posición LEÍDA EN EL MOMENTO del `<audio>` (`currentTime`), sin pasar por el
   * estado React. `pos` se refresca con el evento `timeupdate`, que el browser
   * emite ~4 veces por segundo: para una barra de progreso alcanza, pero para el
   * karaoke NO — el 42% de las palabras de la Audioteca suenan menos de 250 ms,
   * así que muestreando a 4 Hz un ~14% del texto nunca llegaba a marcarse (las
   * palabras "salteadas": the, and, to, his…). Quien necesite precisión de
   * palabra muestrea ESTO por rAF. Ver `useKaraokeIndices`.
   */
  getPositionNow: () => number;
  duration: number;
  playing: boolean;
  /** El user descartó la barra flotante global (X). La sesión sigue viva. */
  dismissed: boolean;
  speed: number;
  /** Opciones de velocidad ofrecidas (0.5×–1.5×). */
  speedOptions: readonly number[];
  loading: boolean;
  error: string | null;

  // Progreso / estados.
  statuses: Record<string, ChapterStatus>;
  statusOf: (ref: ChapterRef) => ChapterStatus;
  progressOf: (ref: ChapterRef) => ChapterProgress | null;
  /**
   * Refresca el mapa de progreso Y devuelve las filas crudas del backend
   * (ordenadas por recencia) — la Audioteca las usa para su "Seguí escuchando"
   * sin pagar un segundo `GET /listen/progress`.
   */
  refreshProgress: (book?: string) => Promise<ProgressEntry[]>;

  /**
   * Capítulos desbloqueados EN ESTA visita (Fase 22), complemento en vivo del
   * `unlocked` del catálogo (que es un snapshot del fetch). Un capítulo está
   * desbloqueado si `kp.unlocked || unlockedKeys[chapterKey(ref)]`.
   */
  unlockedKeys: Record<string, true>;

  // Acciones.
  openChapter: (
    book: CatalogBook,
    chapter: CatalogKeypoint,
    edition: Edition,
    opts?: { play?: boolean; cefr?: Cefr },
  ) => Promise<void>;
  /**
   * Rehidrata la sesión de escucha de ESTA pestaña (snapshot en sessionStorage)
   * tras un reload: reabre el capítulo pausado en la posición exacta. `true` si
   * quedó un capítulo abierto (ya había uno o se restauró); `false` si no hay
   * nada que restaurar (el player muestra el vacío).
   */
  restoreSession: () => Promise<boolean>;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  /** Cierra la barra flotante global: pausa y la oculta (no destruye la sesión). */
  close: () => void;
  /**
   * Termina la sesión de escucha completa (X del player): pausa, persiste la
   * posición y desmonta el capítulo en memoria. El progreso queda en el
   * server, así que "Seguí escuchando" sigue disponible desde biblioteca/home.
   */
  endSession: () => void;
  /**
   * Última sesión de conversación sobre un capítulo (en esta visita), para
   * "Seguir la charla" (resumeSessionId). `null` = todavía no hubo charla.
   */
  coachSessionOf: (ref: ChapterRef) => string | null;
  /** La registra el controller de conversación al salir de una charla listen. */
  rememberCoachSession: (ref: ChapterRef, sessionId: string) => void;
  seek: (sec: number) => void;
  skip: (delta: number) => void;
  setLevel: (cefr: Cefr) => void;
  /**
   * Cambia la voz de narración. `cefr` = nivel donde ESA voz existe (las voces
   * son por libro y nivel: el español está grabado sólo en A1), y cambiarla
   * arrastra el nivel del capítulo. Sin `cefr` se pide en el nivel actual.
   */
  setVoiceId: (voiceSlug: string, cefr?: Cefr) => void;
  setKaraoke: (on: boolean) => void;
  setSpeed: (s: number) => void;
  /** Auto-avanzar al próximo capítulo al terminar (persistido; default on). */
  autoAdvance: boolean;
  setAutoAdvance: (on: boolean) => void;
  /**
   * Countdown del auto-avance en segundos (null = inactivo). Vive en el
   * provider para que el avance funcione con el player minimizado o la
   * pantalla apagada (con la página oculta avanza directo, sin countdown).
   */
  autoAdvanceCountdown: number | null;
  /** Cancela el countdown (cualquier interacción con el panel de fin). */
  cancelAutoAdvance: () => void;
  markConversed: (ref?: ChapterRef) => Promise<void>;
  /** Marca "repasado" (completó una actividad de repaso, 19.G). */
  markReviewed: (ref?: ChapterRef) => Promise<void>;
  /**
   * Reproduce un tramo del audio del capítulo (oración del quiz, 19.G): seek a
   * `start` y pausa automática al llegar a `end` (timeline original; respeta la
   * velocidad sola porque `currentTime` no cambia de escala). Cualquier
   * play/seek manual cancela el corte.
   */
  playSegment: (start: number, end: number) => void;

  // Navegación de capítulo (encadena cruzando audiolibros, en orden de catálogo).
  hasNext: boolean;
  hasPrev: boolean;
  nextUp: NextUp | null;
  goNextChapter: () => void;
  /** Regla estándar: si `pos > 3s` reinicia el capítulo; si no, va al anterior. */
  goPrevChapter: () => void;

  // Temporizador de sueño.
  sleepMode: SleepMode | null;
  /** Segundos restantes del timer (`null` en modo `eoc` o sin timer). */
  sleepSecs: number | null;
  setSleep: (mode: SleepMode, mins?: number) => void;
  cancelSleep: () => void;

  /** Nivel CEFR oficial del user (`cefrGlobal`), default del selector. */
  userLevel: Cefr | null;
}

export const ListenContext = createContext<ListenContextValue | null>(null);

export function useListen(): ListenContextValue {
  const ctx = useContext(ListenContext);
  if (!ctx) throw new Error('useListen debe usarse dentro de <ListenProvider>');
  return ctx;
}

/**
 * ¿Hay sesión de escucha viva y NO descartada? Es la condición que decide si
 * existe la barra flotante global (`GlobalListenBar`, que lo re-exporta) y si
 * las hojas tienen que reservarle la franja donde se apoya (`AppShell`).
 *
 * Fuente única a propósito: quien quiera correrse para no duplicar el control
 * —la fila "sonando" de la card de Escuchar del Home— tiene que preguntar ACÁ y
 * no reimplementar `isOpen && !dismissed`, que es justo el par que se
 * desincroniza en silencio (cerrar la barra con la X deja `isOpen` en true).
 *
 * Vive en este módulo —y no en `GlobalListenBar`— porque lo consume también el
 * shell (`AppShell`), que necesita saber si hay sesión viva para que las hojas
 * le reserven al reproductor la franja donde se apoya: colgarlo del componente
 * de la barra le arrastraba el feature entero (mini-player, router) al shell.
 */
export function useGlobalListenBarActive(): boolean {
  const L = useListen();
  return L.isOpen && !L.dismissed;
}
